import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { CSSProperties } from "@material-ui/styles";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import { ListProps } from "@saleor/types";
import React from "react";
import { FormattedMessage } from "react-intl";

import { transportReport_transportReport_edges_node } from "../../types/transportReport";

const useStyles = makeStyles(
  theme => {
    const overflowing: CSSProperties = {
      overflow: "hidden",
      textOverflow: "ellipsis"
    };
    return {
      colMedium: {
        overflowing,
        width: "15em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colNarrow: {
        overflowing,
        width: "8em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colRow: {
        "& th": {
          fontWeight: 600 as 600,
          paddingBottom: theme.spacing(1),
          paddingTop: theme.spacing(1)
        }
      },
      colSmall: {
        overflowing,
        width: "10em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colWide: {
        overflowing,
        width: "16em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      table: {
        tableLayout: "fixed"
      },
      tableRow: {
        cursor: "pointer"
      }
    };
  },
  { name: "orderLinesList" }
);

interface OrderLinesListProp extends ListProps {
  transportReport: transportReport_transportReport_edges_node[];
}

const TransportReportList: React.FC<OrderLinesListProp> = ({
  transportReport,
  onNextPage,
  onPreviousPage,
  onUpdateListSettings,
  onRowClick,
  pageInfo,
  settings,
  disabled
}) => {
  const colNum = 8;
  const classes = useStyles({});

  return (
    <>
      <ResponsiveTable className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Mall" />
            </TableCellHeader>

            <TableCellHeader className={classes.colSmall}>
              <FormattedMessage defaultMessage="Mother Order Code" />
            </TableCellHeader>

            <TableCellHeader className={classes.colSmall}>
              <FormattedMessage defaultMessage="Order Status" />
            </TableCellHeader>

            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Delivery Type" />
            </TableCellHeader>

            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Courier" />
            </TableCellHeader>

            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Payment Type" />
            </TableCellHeader>

            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Price Paid" />
            </TableCellHeader>
          </TableRow>
        </TableHead>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={colNum}
              settings={settings}
              hasNextPage={
                pageInfo && !disabled ? pageInfo.hasNextPage : undefined
              }
              onNextPage={onNextPage}
              onUpdateListSettings={onUpdateListSettings}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : undefined
              }
              onPreviousPage={onPreviousPage}
            />
          </TableRow>
        </TableFooter>
        <TableBody>
          {transportReport &&
            renderCollection(
              transportReport,
              (transportReport, index) => (
                <TableRow
                  className={!!transportReport ? classes.tableRow : undefined}
                  hover={!!transportReport}
                  key={transportReport ? index : "skeleton"}
                  onClick={
                    transportReport
                      ? onRowClick(
                          btoa(`StoreOrder:${atob(transportReport.id)}`)
                        )
                      : undefined
                  }
                >
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => transportReport.mall_name,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => transportReport.order_code,
                      <Skeleton />
                    )}
                  </TableCell>

                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => transportReport.store_order_status,
                      <Skeleton />
                    )}
                  </TableCell>

                  <TableCell id={`shippingMethodNameRow${index}`}>
                    {maybe<React.ReactNode>(
                      () => transportReport.shipping_method_name.toUpperCase(),
                      <Skeleton />
                    )}
                  </TableCell>

                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => transportReport.courier,
                      <Skeleton />
                    )}
                  </TableCell>

                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => transportReport.payment_method_type,
                      <Skeleton />
                    )}
                  </TableCell>

                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => transportReport.price_paid,
                      <Skeleton />
                    )}
                  </TableCell>
                </TableRow>
              ),
              () => (
                <TableRow>
                  <TableCell colSpan={colNum}>
                    <FormattedMessage defaultMessage="No Transport Report found" />
                  </TableCell>
                </TableRow>
              )
            )}
        </TableBody>
      </ResponsiveTable>
    </>
  );
};

export default TransportReportList;
